import { mapPagination } from '@/shared/mappers';
import { DataWithPagination, Page } from '@/shared/types';
import {
  Process,
  ProcessAutomationsPeformance,
  ProcessBaseInfo,
  ProcessDetails,
  ProcessDetailsWorkflow,
  ProcessDetailsWorkflowTask,
  ProcessItem,
  ProcessWorkflow,
  ProcessWorkflowTask,
  UserProcess,
} from '../domain';
import {
  ProcessAutomationsPerformanceJson,
  ProcessBaseInfoJson,
  ProcessDetailsJson,
  ProcessItemJson,
  ProcessJson,
  UserProcessJson,
} from '../types';

export const mapProcessesPage = (
  page: Page<ProcessJson>,
): DataWithPagination<Process[]> => ({
  items: page.items.map<Process>((item) => ({
    id: item.id ?? 0,
    name: item.name ?? '',
    status: item.status ?? false,
    businessAreaName: item.businessAreaName ?? '',
    organization: item.organization ?? '',
    company: item.company ?? '',
    code: item.code ?? '',
    workflowsCount: item.workflowsCount ?? 0,
    tasksCount: item.tasksCount ?? 0,
    estimatedCost: item.estimatedCost ?? 0,
    canDelete: item.canDelete ?? false,
    runTime: item.runTime ?? 0,
    workflows:
      item.workflows?.map<ProcessWorkflow>((w) => ({
        id: w.id ?? 0,
        name: w.name ?? '',
        code: w.code ?? '',
        order: w.order ?? 0,
        status: w.status ?? false,
        runTime: w.runTime ?? 0,
        tasks:
          w.tasks?.map<ProcessWorkflowTask>((t) => ({
            id: t.id ?? 0,
            name: t.name ?? '',
            periodicityId: t.periodicityId,
            workload: t.workload ?? 0,
            estimatedCost: t.estimatedCost ?? 0,
            status: t.status ?? false,
            code: t.code ?? '',
            usersCount: t.usersCount ?? 0,
            automated: t.automated ?? false,
          })) ?? [],
        totalWorkload: w.totalWorkload ?? 0,
        totalEstimatedCost: w.totalEstimatedCost ?? 0,
        totalUsers: w.totalUsers ?? 0,
      })) ?? [],
    totalWorkload: item.totalWorkload ?? 0,
    totalEstimatedCost: item.totalEstimatedCost ?? 0,
    totalUsers: item.totalUsers ?? 0,
  })),
  ...mapPagination(page),
});

export const mapProcessDetails = (
  process: ProcessDetailsJson,
): ProcessDetails => ({
  id: process.id ?? 0,
  name: process.name ?? '',
  description: process.description ?? '',
  status: process.status ?? false,
  organization: process.organization ?? '',
  company: process.company ?? '',
  businessAreaName: process.businessAreaName ?? '',
  code: process.code ?? '',
  organizationId: process.organizationId ?? 0,
  companyId: process.companyId ?? 0,
  businessAreaId: process.businessAreaId ?? 0,
  workflows:
    process.workflows?.map<ProcessDetailsWorkflow>((w) => ({
      id: w.id ?? 0,
      name: w.name ?? '',
      canDelete: w.canDelete ?? false,
      order: w.order ?? 0,
      tasks:
        w.tasks?.map<ProcessDetailsWorkflowTask>((t) => ({
          id: t.id ?? 0,
          name: t.name ?? '',
          peridocityId: t.periodicityId,
          workload: t.workload ?? 0,
          workloadTypeId: t.workloadTypeId,
          estimatedCost: t.estimatedCost ?? 0,
          status: t.status ?? false,
          code: t.code ?? '',
          usersCount: t.usersCount ?? 0,
          automated: t.automated ?? false,
          canDelete: t.canDelete ?? false,
        })) ?? [],
    })) ?? [],
  manualTime: process.manualTime ?? 0,
  automatedTime: process.automatedTime ?? 0,
  owner: {
    id: process.owner?.id ?? 0,
    firstName: process.owner?.firstName ?? '',
    lastName: process.owner?.lastName ?? '',
    email: process.owner?.email ?? '',
  },
  executionMetrics: {
    totalRuns: process.executionMetrics?.totalRuns ?? 0,
    lastRunTotalSuccessful:
      process.executionMetrics?.lastRunTotalSuccessful ?? 0,
    lastRunTotalBusinessExceptions:
      process.executionMetrics?.lastRunTotalBusinessExceptions ?? 0,
    lastRunTotalSystemExceptions:
      process.executionMetrics?.lastRunTotalSystemExceptions ?? 0,
    lastRunStartDate:
      process.executionMetrics?.lastRunStartDate &&
      new Date(process.executionMetrics?.lastRunStartDate),
    lastRunEndDate:
      process.executionMetrics?.lastRunEndDate &&
      new Date(process.executionMetrics?.lastRunEndDate),
    totalSuccessfulCases: process.executionMetrics?.totalSuccessfulCases ?? 0,
    totalBusinessExceptionsCases:
      process.executionMetrics?.totalBusinessExceptionsCases ?? 0,
    totalSystemExceptionsCases:
      process.executionMetrics?.totalSystemExceptionsCases ?? 0,
  },
  totalWorkload: process.totalWorkload ?? 0,
  totalEstimatedCost: process.totalEstimatedCost ?? 0,
  totalUsers: process.totalUsers ?? 0,
  automationsCount: process.automationsCount ?? 0,
});

export const mapProcessItemsPage = (
  page: Page<ProcessItemJson>,
): DataWithPagination<ProcessItem[]> => {
  return {
    items: page.items.map<ProcessItem>((item) => ({
      id: item.id ?? 0,
      name: item.name ?? '',
      status: item.status ?? false,
      businessAreaId: item.businessAreaId ?? 0,
      businessAreaName: item.businessAreaName ?? '',
      organization: item.organization ?? '',
      organizationId: item.organizationId ?? 0,
      companyId: item.companyId ?? 0,
      company: item.company ?? '',
      code: item.code ?? '',
      workflowsCount: item.workflowsCount ?? 0,
      tasksCount: item.tasksCount ?? 0,
      estimatedCost: item.estimatedCost ?? 0,
      canDelete: item.canDelete ?? false,
    })),
    ...mapPagination(page),
  };
};

export const mapProcessesBaseInfo = (
  processes: ProcessBaseInfoJson[],
): ProcessBaseInfo[] =>
  processes.map((p) => ({
    id: p.id ?? 0,
    name: p.name ?? '',
    status: p.status ?? false,
  }));

export const processAutomationPerformanceMapper = (
  perforrmance: ProcessAutomationsPerformanceJson,
): ProcessAutomationsPeformance => ({
  automationRunTime: perforrmance?.automationRunTime ?? 0,
  totalInvestment: perforrmance?.totalInvestment ?? 0,
  totalReturn: perforrmance?.totalReturn ?? 0,
  averageCost: perforrmance?.averageCost ?? 0,
  predictedManualTime: perforrmance?.predictedManualTime ?? 0,
  totalExecutions: perforrmance?.totalExecutions ?? 0,
});

const userProcessMapper = (userProcessJson: UserProcessJson): UserProcess => ({
  id: userProcessJson.id ?? 0,
  name: userProcessJson.name ?? '',
  status: userProcessJson.status ?? true,
  businessAreaId: userProcessJson.businessAreaId ?? 0,
  businessAreaName: userProcessJson.businessAreaName ?? '',
  organizationId: userProcessJson.organizationId ?? '',
  organization: userProcessJson.organization ?? '',
  companyId: userProcessJson.companyId ?? '',
  company: userProcessJson.company ?? '',
  code: userProcessJson.code ?? '',
  workflowsCount: userProcessJson.workflowsCount ?? 0,
  tasksCount: userProcessJson.tasksCount ?? 0,
  estimatedCost: userProcessJson.estimatedCost ?? 0,
  roles: userProcessJson.roles ?? [],
  canDelete: userProcessJson.canDelete ?? false,
});

const userProcessesMapper = (userProcessJson: UserProcessJson[]) => {
  return userProcessJson.map(userProcessMapper);
};

export const userProcessesPageMapper = (page: Page<UserProcessJson>) => {
  return {
    items: userProcessesMapper(page.items),
    ...mapPagination(page),
  };
};
